import { useEffect, useState } from "react";
import { ThumbsUp, Heart } from "lucide-react";
import { ADD_REACTION } from "@/api/apollo-client/mutations/mutations";
import { useMutation } from "@apollo/client";
import { useAuth } from "@/contexts/authContext";
import countReduce from "@/utils/countReduce";
import { ReactionByMe, ReactionType } from "@/types";
import { useRouter } from "next/navigation";
import Toast from "./Toast";
import { sendClickEvent } from "./Analytics";

type ReactionPopupProps = {
  id: string;
  likes: number;
  isLikedByMe: boolean;
  reaction?: ReactionByMe;
  noReactionList?: boolean; // New prop!
  POST_ACTION_BUTTON_SIZE?: number;
  clips?: boolean; // New prop for clips mode
  onCountClick?: (postId: string) => void; // Optional override for count click
};

export default function ReactionPopup({
  id,
  likes,
  isLikedByMe = false,
  noReactionList = false, // Default false for backward compatibility
  POST_ACTION_BUTTON_SIZE,
  clips = false, // Default false for backward compatibility
  onCountClick,
}: ReactionPopupProps) {
  const { user, isAuthenticated } = useAuth();

  const [isLikedByMeLocal, setLikedByMe] = useState(isLikedByMe);
  const [likesCount, setLikesCount] = useState(likes);
  const [showToast, setShowToast] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const router = useRouter();

  const [AddReaction] = useMutation(ADD_REACTION);

  // Sync local state with server-provided props when id/props change
  useEffect(() => {
    setLikedByMe(isLikedByMe);
    setLikesCount(likes);
  }, [id, likes, isLikedByMe]);

  const handleReaction = async (reactionType: ReactionType) => {
    if (!isAuthenticated) {
      router.push("/login");
      return;
    }

    if (!user || !user.id) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    // Start animation
    setIsAnimating(true);

    try {
      const variables = {
        reaction_by: user.id,
        reaction_on: id,
        ...(!isLikedByMeLocal ? { reaction_type: reactionType } : {}),
      };
      await AddReaction({ variables });

      sendClickEvent({
        category: "Button",
        action: "post_like",
        label: "Liked a post",
        value: 1,
        extraParams: {
          postId: id,
        },
      });

      // Toggle like state and adjust count
      if (!isLikedByMeLocal) {
        setLikesCount(likesCount + 1);
      } else {
        setLikesCount(likesCount - 1);
      }
      setLikedByMe(!isLikedByMeLocal);

      // End animation after a short delay
      setTimeout(() => setIsAnimating(false), 300);
    } catch (error) {
      console.error("Error adding reaction:", (error as Error).message);
      setIsAnimating(false);
    }
  };

  const handleLikeCountClick = (postId: string) => {
    if (noReactionList) return; // Do nothing if noReactionList is true
    if (onCountClick) {
      onCountClick(postId);
      return;
    }
    router.push(`/post/${postId}/reactions`);
  };

  // Choose icon based on clips mode
  const IconComponent = clips ? Heart : ThumbsUp;

  return (
    <>
      {clips ? (
        // Clips mode: vertical layout with heart icon and likes below
        <div className="flex flex-col items-center">
          <div
            className={`flex items-center justify-center text-sm py-1 transition-all duration-100 ease-out ${
              isLikedByMeLocal
                ? "text-red-500"
                : "text-white hover:text-red-400"
            }`}
          >
            <IconComponent
              {...(isLikedByMeLocal
                ? {
                    fill: "currentColor",
                    strokeWidth: 1,
                    size: POST_ACTION_BUTTON_SIZE,
                  }
                : { size: POST_ACTION_BUTTON_SIZE })}
              onClick={() => handleReaction(ReactionType.LIKE)}
              className={`cursor-pointer transition-all duration-100 ease-out ${
                isAnimating
                  ? "scale-125 transform rotate-12"
                  : "scale-100 transform rotate-0"
              } hover:scale-110`}
            />
          </div>
          <div
            onClick={() => handleLikeCountClick(id)}
            className={
              `mt-1 text-xs font-semibold rounded-full transition-all duration-100 ease-out ` +
              (isLikedByMeLocal ? "text-red-500" : "text-white") +
              (!noReactionList
                ? " cursor-pointer"
                : " cursor-default opacity-60 pointer-events-none")
            }
            title={noReactionList ? undefined : "View all reactions"}
          >
            <span
              className={`inline-block transition-all duration-300 ease-out ${
                isAnimating ? "scale-110 text-red-400" : "scale-100"
              }`}
            >
              {countReduce(likesCount)}
            </span>
          </div>
        </div>
      ) : (
        // Default mode: horizontal layout with thumbs up icon and likes beside
        <div className="flex items-center gap-1">
          {/* Countable Like Button */}
          <div
            className={`flex items-center gap-1 text-sm py-1 transition-all duration-300 ease-out ${
              isLikedByMeLocal
                ? "text-yellow-600"
                : "text-gray-400 hover:text-grey-600"
            }`}
          >
            <IconComponent
              {...(isLikedByMeLocal
                ? {
                    fill: "gold",
                    strokeWidth: 1,
                    color: "black",
                    size: POST_ACTION_BUTTON_SIZE,
                  }
                : { size: POST_ACTION_BUTTON_SIZE })}
              onClick={() => handleReaction(ReactionType.LIKE)}
              className={`cursor-pointer transition-all duration-300 ease-out ${
                isAnimating
                  ? "scale-125 transform -rotate-12"
                  : "scale-100 transform rotate-0"
              } hover:scale-110`}
            />
          </div>
          <div
            onClick={() => handleLikeCountClick(id)}
            className={
              `pr-1.5 py-1 text-lg font-bold rounded-full transition-all duration-300 ease-out ` +
              (isLikedByMeLocal ? "text-yellow-600" : "text-grey-600") +
              (!noReactionList
                ? " cursor-pointer"
                : " cursor-default opacity-60 pointer-events-none")
            }
            title={noReactionList ? undefined : "View all reactions"}
          >
            <span
              className={`inline-block transition-all duration-300 ease-out ${
                isAnimating ? "scale-110 text-yellow-500" : "scale-100"
              }`}
            >
              {countReduce(likesCount)}
            </span>
          </div>
        </div>
      )}

      {/* Toast for authentication requirement */}
      <Toast
        isVisible={showToast}
        title="Authentication Required"
        message="Please signup to like posts"
        type="failure"
      />
    </>
  );
}
